import { useAuth0 } from "@auth0/auth0-react";
import { Invitation, InvitationsResponse } from "../types/invitation.types";
import { useCallback } from "react";

export const useInvitationService = () => {
  const { getAccessTokenSilently } = useAuth0();
  const apiUrl = import.meta.env.VITE_API_SERVER_URL;

  const getAuthHeader = useCallback(async () => {
    const token = await getAccessTokenSilently({
      authorizationParams: {
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
      },
    });
    return { Authorization: `Bearer ${token}` };
  }, [getAccessTokenSilently]);

  const createInvitation = useCallback(
    async (payload: {
      email: string;
      assigned_plan_code?: string;
      tenant_id?: string;
      tenant_name?: string;
    }): Promise<Invitation> => {
      const authHeader = await getAuthHeader();
      const requestBody: Record<string, string> = { email: payload.email };
      if (payload.assigned_plan_code) requestBody.assigned_plan_code = payload.assigned_plan_code;
      if (payload.tenant_id) requestBody.tenant_id = payload.tenant_id;
      if (payload.tenant_name) requestBody.tenant_name = payload.tenant_name;

      const response = await fetch(`${apiUrl}/api/invitations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeader,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail ?? errorData.message ?? "Failed to create invitation");
      }

      return response.json();
    },
    [apiUrl, getAuthHeader]
  );

  const getAllInvitations = useCallback(async (): Promise<InvitationsResponse> => {
    const authHeader = await getAuthHeader();
    
    const response = await fetch(`${apiUrl}/api/invitations`, {
      method: 'GET',
      headers: {
        ...authHeader,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to get invitations');
    }

    return response.json();
  }, [apiUrl, getAuthHeader]);

  const validateInvitation = useCallback(async (token: string): Promise<Invitation> => {
    const response = await fetch(`${apiUrl}/api/invitations/validate/${token}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Invalid or expired invitation');
    }

    return response.json();
  }, [apiUrl]);

  const acceptInvitation = useCallback(async (
    token: string,
    options?: { authenticated?: boolean }
  ): Promise<Invitation> => {
    let headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (options?.authenticated) {
      const authHeader = await getAuthHeader();
      headers = {
        ...headers,
        ...authHeader,
      };
    }

    const response = await fetch(`${apiUrl}/api/invitations/accept`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ invitation_token: token }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to accept invitation');
    }

    return response.json();
  }, [apiUrl, getAuthHeader]);

  const deleteInvitation = useCallback(async (invitationId: string): Promise<boolean> => {
    const authHeader = await getAuthHeader();
    
    const response = await fetch(`${apiUrl}/api/invitations/${invitationId}`, {
      method: 'DELETE',
      headers: {
        ...authHeader,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to delete invitation');
    }

    return true;
  }, [apiUrl, getAuthHeader]);

  const isInvited = useCallback(async (): Promise<boolean> => {
    const authHeader = await getAuthHeader();
    
    const response = await fetch(`${apiUrl}/api/invitations/is-invited`, {
      method: 'GET',
      headers: {
        ...authHeader,
      },
    });

    if (!response.ok) {
      if (response.status === 401 && typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('invirtus:auth-unauthorized', { detail: { source: 'isInvited' } })
        );
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to check if user is invited');
    }

    return response.json();
  }, [apiUrl, getAuthHeader]);

  /**
   * Check the workspace (tenant) status for the current user.
   * Returns "active", "suspended", etc. or null if no workspace.
   */
  const getWorkspaceStatus = useCallback(async (): Promise<string | null> => {
    try {
      const authHeader = await getAuthHeader();
      const response = await fetch(`${apiUrl}/api/workspace/members`, {
        method: 'GET',
        headers: { ...authHeader },
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data.workspace_status ?? null;
    } catch {
      return null;
    }
  }, [apiUrl, getAuthHeader]);

  return {
    createInvitation,
    getAllInvitations,
    validateInvitation,
    acceptInvitation,
    deleteInvitation,
    isInvited,
    getWorkspaceStatus,
  };
};